// What's new — the plain-language release notes behind /whats-new.
//
// Written by hand (by Claude, in the same PR as the change — see CLAUDE.md),
// newest first. CI fails a `feat:` PR that touches web/src without editing
// this file, unless the PR opts out ([no-whats-new] or the skip-whats-new
// label). CHANGELOG.md stays the developer record; this is for the people
// using the app: what they can now do, in their words, no version numbers.
//
// `module` + `min` gate an entry the way a route is gated, so a member never
// reads about payroll locks. No module = everyone. It hides; it grants
// nothing — the feature itself is still RLS-gated.
import { can, isModuleEnabled, type PermLevel, type PermMap } from './roles';

export type WhatsNewEntry = {
  /** 'YYYY-MM-DD', the day it shipped. */
  date: string;
  title: string;
  body: string;
  module?: string;
  /** Lowest permission on `module` that can use it. Default 'view'. */
  min?: PermLevel;
  /** Where to try it. */
  to?: string;
  /** A further check on top of `module`, for a page no single module gates (as the nav's `also`). */
  also?: (perms: PermMap) => boolean;
};

export const WHATS_NEW: WhatsNewEntry[] = [
  {
    date: '2026-11-04',
    title: 'Set a project’s stage, status and amount',
    body: 'Edit project details now sets the stage, status, health, priority and whether the project is billable. Owners and admins can type in the deal amount there until a Zoho deal is linked; after that, Zoho’s amount is used. You can also click a stage at the top of a project page to move it there.',
    module: 'projects',
    min: 'edit',
    to: '/projects',
  },
  {
    date: '2026-11-04',
    title: 'See what each project earns and costs',
    body: 'Owners and admins have a new Profitability page under Projects. It shows each project’s deal value, what its hours have cost in salary, its margin and how much of the deal is used up, plus each person’s utilisation against the studio’s target (80% to start) and what idle time costs. Freelancers count toward how busy people are but are left out of cost, and deal values show once Zoho is connected.',
    module: 'projects',
    // The route's gate: projects plus seesMoney (shell.tsx), inline to dodge an import cycle.
    also: (p) => can(p, 'people', 'full') && can(p, 'pipeline', 'full'),
    to: '/profitability',
  },
  {
    date: '2026-11-04',
    title: 'Utilisation counts this month so far',
    body: 'Utilisation on the Dashboard, Capacity and Talent now covers this month so far, up to yesterday. It counts only the hours you’re available after approved leave, and is measured against the studio’s target.',
    module: 'capacity',
    to: '/capacity',
  },
  {
    date: '2026-11-03',
    title: 'Edit a whole day in one place',
    body: 'Edit day puts a person’s check-ins and check-outs, any missed session, their credited hours and their project time in one dialog. HR and their manager change it directly and they’re told what changed and why; they can ask for the same change, and you approve it, adjust it first, or decline it. Turn it on for HR and managers, then everyone, under Org settings → Work time.',
    module: 'people',
    min: 'full',
    to: '/org?tab=hours',
  },
  {
    date: '2026-11-03',
    title: 'A calmer light theme',
    body: 'In light theme, cards, pop-ups and the page behind them are now one cool white instead of beige on blue, and cards have a firmer edge so they no longer fade into the page. Dark theme is unchanged.',
  },
  {
    date: '2026-11-03',
    title: 'Nobody approves their own leave',
    body: 'Your own leave request now waits for someone else who approves leave, whoever you are, and the owner’s too. It shows as yours in the approval queue, Approve all leaves it out, and the Requests badge now counts only what is waiting on you, the same number in the sidebar and on the tab.',
    module: 'people',
    min: 'full',
    to: '/hr?view=requests',
  },
  {
    date: '2026-11-03',
    title: 'Stay signed in on your phone and your laptop',
    body: 'Signing in on one device no longer signs you out of the others, and Sign out now ends only the session on the device you’re using. Changing your password still signs you out everywhere.',
  },
  {
    date: '2026-11-03',
    title: 'Waking your computer no longer checks you back in',
    body: 'After you check out, the Check in again button no longer holds the keyboard. A key pressed to wake a sleeping computer used to press it and quietly start a new session. Checking in again is still one click, or one Tab away.',
    module: 'timesheets',
    to: '/timesheets',
  },
  {
    date: '2026-10-23',
    title: 'A tidier leave history',
    body: 'How this balance was reached no longer lists the back-and-forth from the comp off rule changes. Those lines cancelled each other out, so now you see only the leave you took and the real changes to your balance, on screen and in the CSV.',
    to: '/me',
  },
  {
    date: '2026-10-22',
    title: 'Comments, files and links on Jira work items go both ways',
    body: 'On a project linked to a client’s Jira, comment on a work item here and it’s posted in their Jira as the hyprflow account, with your name first; their comments show here. Jira’s files open straight from Jira, and you can link Jira work items to each other. Files are still added in Jira.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-22',
    title: 'Changes you make to Jira-linked work items now go to Jira',
    body: 'On a project linked to a client’s Jira you can move cards, edit work items and create new ones here, and each change goes straight to their Jira, where it shows as the hyprflow account. Changes made in Jira show here within a minute. Work items are still deleted in Jira.',
    module: 'projects',
    min: 'edit',
    to: '/projects',
  },
  {
    date: '2026-10-22',
    title: 'Comp off no longer changes your Casual leave',
    body: 'Comp off is counted on its own: days you used stay used. A month’s overtime credits comp off only while your Casual has room under its 12 days (12 less the Casual days you have left, less comp off already credited this year); overtime past that isn’t counted. Casual days given back on 19 Oct are charged again.',
    to: '/me',
  },
  {
    date: '2026-10-22',
    title: 'Project tabs look like the rest of the app',
    body: 'A project’s tabs now use the same bar as People and Organisation, each with its icon. Recent activity on the Summary shows the five latest changes instead of a list that kept growing.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: 'Posts is your project’s team feed',
    body: 'Share an update, a milestone or a blocker with everyone on a project from its Posts tab, with @mentions, reactions and replies. The project’s lead can post announcements and pin what matters to the top. Blockers and announcements reach the whole team; client reports stay on the Reports page.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: 'Descriptions save as you type',
    body: 'A work item’s description now saves by itself a moment after you stop typing, and at once when you click away or press Esc. There’s no Save button any more. If a save fails, your text stays in the editor with a Retry.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: 'Drag your board’s columns into order',
    body: 'A project’s lead can now drag a Board column by its header to reorder the workflow, or move it with the column’s ••• Move left and Move right. Add a column with + Create status at the end of the board. Dropping a card between two others works again everywhere, also after its status was changed from the work item or the List. On a project linked to Jira, change statuses in Jira.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: '+ Task opens the full work item',
    body: '+ Task now opens the full work item view, with the description and every field. The item is made as soon as you give it a title, and everything after that saves by itself. Close it without a title and nothing is created.',
    module: 'projects',
    min: 'edit',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: 'Replies quote the comment they answer',
    body: 'Reply on a work item’s comment now shows the comment you answered above your reply; click it to jump there. Logged hours appear in the work item’s Activity and Work log as soon as a timer stops, not only after you check out.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: 'Edit a project, and tabs like Jira',
    body: 'A project’s lead now has an Edit project button in the header; the details point to the Team tab for members and to the Zoho deal. The project tabs are a full-width row, and clicking a row in the List opens that work item.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: 'Timeline bars sit on their own dates',
    body: 'Every bar on a project’s Timeline now runs from its own start date to its due date. An epic is no longer drawn over its children’s dates; where children run past it, a faint extension shows it, and you can drag the epic itself. The Board says when epics aren’t shown there.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-19',
    title: 'Casual stays at 12; comp off gives back days you used',
    body: 'Comp off no longer adds to your Casual leave. It gives back Casual days you have already used, never more than you used, so Casual stays at its 12 days and your days left never go above 12. Your comp-off count still shows everything you earned.',
    to: '/me',
  },
  {
    date: '2026-10-16',
    title: 'Your dashboard lists every work item due',
    body: 'Work items due now shows Hyprflow’s own work items beside Jira’s, each opening where it lives, and projects show their key everywhere, Schedule and the timer included.',
    module: 'dashboard',
    to: '/dashboard',
  },
  {
    date: '2026-10-16',
    title: 'Comp off from your month’s surplus, into Casual',
    body: 'A month’s comp off now comes from your surplus: effective hours (clock plus credited) over the month’s required hours, half a day per 3.5 hours, at most 2 days a month. It goes into your Casual leave up to Casual’s yearly standard of days left, and your comp-off count keeps all of it. Every closed month since June was re-counted this way; this month counts when payroll closes it.',
    to: '/me',
  },
  {
    date: '2026-10-16',
    title: 'Drag work items across your project’s board',
    body: 'On a project’s Board, drag a card to another column or up and down its own, or use the card’s ••• to move it, assign it to yourself or copy its link. Add work right in a column with + Create, group the board into swimlanes by assignee, priority, category or epic, and pick which fields cards show. A project’s lead can add, rename, reorder and delete its columns.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'See and edit your project’s work in a list',
    body: 'A project’s List tab shows every work item as a table, with subtasks under their parent. Click a cell to change its status, assignee, dates, labels or estimate in place: Enter saves and moves down, Tab moves right. Group it by assignee, status, priority and more, pick your columns, sort by any of them, and tick several items to edit them together.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Plan work on a timeline',
    body: 'A project’s Timeline tab shows each work item as a bar from its start date to its due date, with epics above their stories and tasks. Drag a bar to move it or its ends to change the dates, or use the arrow keys; give an undated item a week with Schedule. Draw a line from one bar to another to say one blocks the other, and drag a row to change the order the board shows.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Schedule shows every work item’s due date',
    body: 'Each project’s row on Schedule now marks the due dates of its own work items as well as Jira’s, and its bar shows the client, the lead and an at-risk or blocked flag as before.',
    module: 'schedule',
    to: '/gantt',
  },
  {
    date: '2026-10-16',
    title: 'See due work on a calendar',
    body: 'A project’s Calendar tab shows each work item across its days, from its start date to its due date, with overdue ones in rose. Drag one to another day, or drag an end to change only that date; click a day to create a work item due then. Unscheduled work lists everything with no due date: drag it onto a day to schedule it.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'See how a project is going, on its Summary and Reports',
    body: 'A project’s Summary shows what was completed, updated and created in the last week and what is due soon, with how its work splits by status, priority, type and person, and what happened lately. Reports draws created against resolved, the flow of work through its statuses, who has what open, time logged against estimates, and how old open work is, each with its numbers a click away.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Find every file linked in a project',
    body: 'A project’s Files tab lists every Google Drive link added to its work items, with the work item it’s on, who added it and when. Filter by who added it, the kind of file or when, open any of them in Drive, or add a link to a work item from there. Files stay in Drive; Hyprflow keeps the link.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Time your work against a work item',
    body: 'On projects run in Hyprflow, the timer now asks which work item you’re working on. Pick one in Start timer, or create it right there, or press the play button on an open work item. The time you log shows on the work item: its logged hours and, in Activity, its Work log, for everyone on the project. Jira-linked projects work as before.',
    module: 'projects',
    min: 'edit',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Comment, mention teammates, watch and add file links on work items',
    body: 'A work item’s Activity shows its comments, its history and the time logged on it, newest or oldest first. Comment with headings, lists and @mentions (the person you mention is told), use a quick reply like “Looks good!”, and edit or delete your own comments. Watch a work item with the eye to hear about its comments and status changes, and add its files as Google Drive links.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Open any work item to edit it like in Jira',
    body: 'Open a work item and change it right where you read it: the summary, a description with headings, lists, links and @mentions, the status, assignee, priority, dates, labels, estimate, epic, category and team. Add subtasks and link it to other work items, or expand it to a full page. Create work item takes all of this at once, and Create another keeps it open for the next one.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Hear when a task is assigned to you',
    body: 'When someone assigns you a task in one of your projects, it shows in your notifications with a link to the task. You aren’t told about tasks you assign to yourself, or about assignments that come in from Jira.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'A new project page, with a board you can search and filter',
    body: 'A project’s page has the new look: Team, Brief, Auto-assign and + Task at the top, its figures as tiles, and every view as a tab. The board follows the project’s own workflow; search it by summary or key, use Assigned to me, Due this week and Done items, or filter by assignee, status, priority, labels and more, and your filters are kept for next time. Open a work item from its card, create one with + Task or C, and press ? for every shortcut.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-16',
    title: 'Create projects and choose who works on them',
    body: 'New project, on Projects, sets one up here with a name, a short key its work items are numbered by, a lead and the people on it as Lead, Member or Viewer. A Zoho deal is optional. Projects has a new look, with a list or cards and a star for the projects you follow. On a project, its lead can add and remove people on the Team tab, change their roles, and edit the project’s details.',
    module: 'projects',
    min: 'full',
    to: '/projects',
  },
  {
    date: '2026-10-15',
    title: 'Send your daily report from My space',
    body: 'My space has a Daily report card for today. Check the draft your assistant wrote, or start one from your check-out summary and project time, edit it, and send it to everyone above you. If people report to you, Team reports lists what they’ve sent, by person and date.',
    to: '/me',
  },
  {
    date: '2026-10-15',
    title: 'Requests that wait too long are escalated',
    body: 'A leave request or time correction still waiting after 2 working days is flagged once to HR and to the approvers’ managers. Requests & approvals lists what was escalated and how it ended. Change the limit under Organisation, Leave policy.',
    module: 'people',
    min: 'full',
    to: '/hr?view=requests',
  },
  {
    date: '2026-10-15',
    title: 'Read and acknowledge company policies',
    body: 'When HR publishes a policy for you, My space lists it with a link to the document in Drive and an Acknowledge button, and the card at the foot of the sidebar shows how many are waiting. A new version asks again. HR sees who has acknowledged each one under People, Requests & approvals.',
    to: '/me',
  },
  {
    date: '2026-10-15',
    title: 'Keep your certificates on record',
    body: 'My space has a Certificates card: add a certificate with its issuer, dates and a link to it in Drive. You and HR are reminded 30 days and 7 days before it expires, and on the day it does. Your manager can see them too, and HR can add them for you.',
    to: '/me',
  },
  {
    date: '2026-10-15',
    title: 'A dashboard for your role',
    body: 'Dashboard shows your projects in flight with their delivery dates, Jira tasks due soon, this week’s project hours and your running timer. Leads see their team’s work and managers see their projects’ deal value. Owners and admins choose what each role sees in Organisation → Access.',
    module: 'dashboard',
    to: '/dashboard',
  },
  {
    date: '2026-10-15',
    title: 'Email a client their report',
    body: 'On Reports, a project’s manager can now send an approved report to the client by email. Send shows who receives it, and you can add or change those addresses there; they’re kept for the project’s next reports. Drafts now also cover projects at risk or blocked.',
    module: 'reports',
    to: '/reports',
  },
  {
    date: '2026-10-15',
    title: 'Talent now shows each person’s week, utilisation and trends',
    body: 'Talent’s tiles show the studio’s average utilisation and hours a week, with how they moved since the week before. Pick someone to see a ring of this week’s project hours against the hours they’re here, their week day by day, and their skills by level. Schedule opens the timeline with just their projects.',
    module: 'talent',
    to: '/talent',
  },
  {
    date: '2026-10-15',
    title: 'Capacity: skill forecast, trends and Suggest mix',
    body: 'Capacity now shows how booked each skill is over the next 12 weeks (switch to People for each person’s free hours), tiles with 12-week trends, and a card per person with their utilisation and the week they’re next free. Suggest mix lists who has the skills a project needs and time this week.',
    module: 'capacity',
    to: '/capacity',
  },
  {
    date: '2026-10-15',
    title: 'Export Capacity to a spreadsheet',
    body: 'Export CSV on Capacity downloads each person’s free, away and tentative hours for the next 12 weeks, for whoever your search and skill filter show.',
    module: 'capacity',
    to: '/capacity',
  },
  {
    date: '2026-10-15',
    title: 'Review client reports before they go out',
    body: 'Reports lists each project’s nightly draft by day, marked Draft, Approved or Sent, with filters for project and client. A project’s manager can edit a draft and approve it. Nothing is emailed to clients yet.',
    module: 'reports',
    to: '/reports',
  },
  {
    date: '2026-10-15',
    title: 'Projects are back',
    body: 'Projects lists every project you’re on, as a table or as cards, with filters for at-risk, blocked and shipped work and a countdown to each delivery date. A project’s page shows its dates, manager and stage, and the tasks mirrored from Jira; open a task to see it, or jump to it in Jira.',
    module: 'projects',
    to: '/projects',
  },
  {
    date: '2026-10-15',
    title: 'Start a timer on a project',
    body: 'Start timer, next to Check in at the top, times your work on one of your projects (on a Jira project, on one of its issues). It checks you in if you aren’t yet; stopping it keeps you checked in, and you can switch projects without stopping. Timesheets shows each day split by project, with what was billable.',
    module: 'projects',
    min: 'edit',
    to: '/me',
  },
  {
    date: '2026-10-15',
    title: 'Talent: everyone’s skills and week in one place',
    body: 'Talent lists your colleagues with their top skills and level. Pick someone to see their free hours this week and, where you can see their time, their logged week and how much of it went on projects. If someone reports to you, you can set their skills and levels from the studio’s skill list.',
    module: 'talent',
    to: '/talent',
  },
  {
    date: '2026-10-15',
    title: 'See who is free in the next 12 weeks',
    body: 'Capacity shows each person’s free hours, week by week, for the next 12 weeks: their contracted hours less approved leave and holidays. Weeks with leave are marked, and leave still waiting for approval shows as tentative. Search by name or filter by skill to find someone.',
    module: 'capacity',
    to: '/capacity',
  },
  {
    date: '2026-10-15',
    title: 'See every project on one schedule',
    body: 'Schedule shows each project you can see as a bar from its start date to its deadline, with its Jira due dates marked as diamonds and today highlighted. Switch between days and weeks, and open a project from its row.',
    module: 'schedule',
    to: '/gantt',
  },
  {
    date: '2026-10-13',
    title: 'Managers can set the times of a forgotten day',
    body: 'When someone who reports to you forgets to check out, My space lists the day and you can set when they really came and left, with a reason. HR can do the same from the person’s Hours. The person is told who changed it and why.',
    to: '/me',
  },
  {
    date: '2026-10-12',
    title: 'Told when a day is closed for you',
    body: 'If you forget to check out, the day is still closed for you overnight, and now you get a notification saying so. Check the times on Timesheets and request a correction if they are wrong. Your manager and HR are told too.',
    to: '/timesheets',
  },
  {
    date: '2026-10-12',
    title: 'Your job title in the sidebar',
    body: 'The card at the bottom of the sidebar now shows your job title under your name. People search has a magnifier, and the steps at the top of a page show when you can click back to them.',
  },
  {
    date: '2026-10-12',
    title: 'What’s new is here',
    body: 'This page lists what changed in Hyprflow OS, newest first. A “New” tag in the sidebar tells you when something has been added since your last visit.',
  },
  {
    date: '2026-10-12',
    title: 'Managers can credit hours to their team',
    body: 'If people report to you, My space has a card for crediting extra hours to them directly, without going through HR.',
    to: '/me',
  },
  {
    date: '2026-10-12',
    title: 'Credit hours to several people at once',
    body: 'Select people on the People dashboard and credit hours to all of them in one go. A credit that went in wrong can now be edited from the credit history instead of revoked and redone.',
    module: 'people',
    min: 'edit',
    to: '/hr',
  },
  {
    date: '2026-10-09',
    title: 'One queue for every approval',
    body: 'Leave requests, time corrections and comp-hour claims now wait in one Requests & approvals list, so nothing is missed in a separate tab.',
    module: 'people',
    min: 'edit',
    to: '/hr',
  },
  {
    date: '2026-10-09',
    title: 'Waive a small payroll shortfall',
    body: 'When someone is a little short on hours for the month, HR can waive it, or choose whether the rest comes out of leave or pay.',
    module: 'people',
    min: 'full',
    to: '/hr',
  },
  {
    date: '2026-10-09',
    title: 'Comp off in your leave balance',
    body: 'Comp-off days you have earned now show beside your other leave in My space, with how many are left to use.',
    to: '/me',
  },
  {
    date: '2026-10-09',
    title: 'Your leave on the calendar',
    body: 'Approved leave shows on the Workspace calendar as an all-day block, so you can see your time off next to everything else.',
    module: 'calendar',
    to: '/workspace',
  },
  {
    date: '2026-10-09',
    title: 'Fix a day by its check-in and check-out times',
    body: 'Forgot to check out, or checked in late by mistake? Ask for a correction by entering the right check-in and check-out times for that day.',
    module: 'timesheets',
    to: '/timesheets',
  },
  {
    date: '2026-10-08',
    title: 'Overtime turns into comp off at month end',
    body: 'Extra hours you work over the month are added up when payroll closes and credited as comp-off days you can take as leave.',
    to: '/me',
  },
  {
    date: '2026-10-07',
    title: 'Hyprflow on your phone',
    body: 'The whole app now fits a phone screen: the menu opens from the top bar, and checking in and applying for leave sit within thumb reach.',
  },
  {
    date: '2026-09-29',
    title: 'Google Calendar every 15 minutes',
    body: 'Events from the company Google Calendar now reach Workspace within 15 minutes instead of 2 hours.',
    module: 'calendar',
    to: '/workspace',
  },
  {
    date: '2026-09-21',
    title: 'The company Google Calendar in Workspace',
    body: 'Company events from Google Calendar show up in Workspace, and Hyprflow events go back to Google. You can also subscribe to your own calendar link from your phone or Outlook.',
    module: 'calendar',
    to: '/workspace',
  },
];

export const visibleWhatsNew = (perms: PermMap | undefined): WhatsNewEntry[] =>
  WHATS_NEW.filter(
    (e) =>
      !e.module ||
      (isModuleEnabled(e.module) &&
        can(perms, e.module, e.min ?? 'view') &&
        (!e.also || (!!perms && e.also(perms)))),
  );

// Per browser (owner, 2026-10-12): the newest entry this person has seen.
// A key, not a date, so a second entry on the same day still counts as new.
const SEEN_KEY = 'hf.whatsNewSeen';
const keyOf = (e: WhatsNewEntry) => `${e.date} ${e.title}`;

export const hasUnseenWhatsNew = (perms: PermMap | undefined): boolean => {
  const newest = visibleWhatsNew(perms)[0];
  if (!newest) return false;
  try {
    return localStorage.getItem(SEEN_KEY) !== keyOf(newest);
  } catch {
    return false;
  }
};

export const markWhatsNewSeen = (perms: PermMap | undefined): void => {
  const newest = visibleWhatsNew(perms)[0];
  if (!newest) return;
  try {
    localStorage.setItem(SEEN_KEY, keyOf(newest));
  } catch {
    // Private mode / blocked storage: the tag just stays.
  }
};
